import { DateTime } from 'luxon';

/** Minutes past midnight → FullCalendar's `HH:mm:ss` slot string. */
export function minutesToSlot(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

export const HALF_HOUR = 30;

export const floorTo = (minute: number, step: number) => Math.floor(minute / step) * step;
export const ceilTo = (minute: number, step: number) => Math.ceil(minute / step) * step;

export interface DayWindow {
  slotMin: number;
  slotMax: number;
  scrollTime: string;
}

/**
 * Visible day window + scroll target, shared by the Plan and Daily grids.
 *
 * Slot boundaries snap to the hour. FullCalendar labels rows from
 * `slotMinTime` onward, so a window that opens at 11:14 labels the whole grid
 * 11:14 / 11:44 / 12:14, technically correct and unreadable. Times on a
 * calendar have to land where the eye expects them.
 *
 * The window still widens past day_start/day_end when "now" falls outside it,
 * or the now-indicator is clipped off the grid entirely.
 */
export function planDayWindow(
  timezone: string,
  dayStart: number,
  dayEnd: number,
  { anchorToNow = true, now = DateTime.now() }: { anchorToNow?: boolean; now?: DateTime } = {},
): DayWindow {
  const zoneNow = now.setZone(timezone);
  const nowMins = zoneNow.hour * 60 + zoneNow.minute;

  let slotMin = dayStart;
  let slotMax = Math.max(dayEnd, dayStart + 60);

  if (nowMins < slotMin) slotMin = Math.max(0, nowMins - 30);
  if (nowMins >= slotMax) slotMax = Math.min(24 * 60, nowMins + 60);

  slotMin = floorTo(slotMin, 60);
  slotMax = Math.min(24 * 60, ceilTo(slotMax, 60));

  // Looking at today: open where the day is. Looking ahead: open at the top of
  // the working day, because nothing has happened there yet.
  let scrollMins = anchorToNow ? nowMins - 30 : dayStart;
  if (scrollMins < slotMin) scrollMins = slotMin;
  const latest = Math.max(slotMin, slotMax - 60);
  if (scrollMins > latest) scrollMins = latest;

  return {
    slotMin,
    slotMax,
    scrollTime: minutesToSlot(floorTo(scrollMins, HALF_HOUR)),
  };
}
